import { Stack } from '@heathmont/moon-components';
import { Controller } from 'react-hook-form';
import FormCheckbox from '~/client/shared/form/FormCheckbox';
import FormError from '~/client/shared/form/FormError';
import FormInput from '~/client/shared/form/FormInput';
import FormPhoneNumber from '~/client/shared/form/FormPhoneNumberStepByStep/FormPhoneNumber';
import FormReCAPTCHA from '~/client/shared/form/FormReCAPTCHA';
import FlexStack from '~/client/shared/styles/FlexStack';
import Divider from './Divider';
import IAgreeToTermsText from './IAgreeToTermsText';
import MarketingOffers from './MarketingOffers';
import MinimumAgeLimitText from './MinimumAgeLimitText';
import useFinishSignupStep from '../hooks/useFinishSignupStep';
import SubmitButtonFormItem from '../shared/SubmitButtonFormItem';

type Props = {
  isPopover?: boolean;
  onSubmitHandle?: () => void;
};

const FinishSignupStep: FC<Props> = ({ onSubmitHandle, isPopover = false }) => {
  const {
    control,
    errors,
    loading,
    isCaptchaRequired,
    recaptchaRef,
    required,
    optional,
    handleSubmit,
    isSubmitDisabled,
    termsLabel,
    offersLabel,
    handleDateOfBirthchange,
    inputSize,
    socialAuthType,
    isEmailRequired,
    isWalletAuthConnection,
    handleBlur,
  } = useFinishSignupStep({ onSuccess: onSubmitHandle, isPopover });

  return (
    <form>
      <Stack>
        <Stack>
          <Stack space={0}>
            <FormInput
              {...required('username')}
              type="text"
              isErrorTop
              name="username"
              isSharpBottomSide
              placeholder="general.placeholder.username"
              label="profile.username"
              error={errors?.username?.message}
              inputSize={inputSize}
            />
            <Controller
              control={control}
              name="dateOfBirth"
              render={() => (
                <FormInput
                  type="date"
                  name="dateOfBirth"
                  data-testId="dateOfBirth"
                  onBlur={handleBlur}
                  onChange={handleDateOfBirthchange}
                  isSharpTopSide
                  label="profile.date_of_birth"
                  error={errors?.dateOfBirth?.day?.message}
                  inputSize={inputSize}
                />
              )}
            />
          </Stack>
          <MinimumAgeLimitText />
        </Stack>
        {(!socialAuthType || isEmailRequired) && (
          <FormInput
            {...required('email')}
            type="email"
            name="email"
            label="profile.email"
            placeholder="general.placeholder.email"
            autoCapitalize="off"
            error={errors?.email?.message}
            inputSize={inputSize}
          />
        )}
        <FormPhoneNumber
          control={control}
          {...optional('phone')}
          name="phone"
          label="profile.phone"
          placeholder="general.placeholder.phone_number"
          selectInputLabel="profile.signup.country_region"
          error={errors?.phone?.message}
        />
        {(!socialAuthType || isWalletAuthConnection) && (
          <FormInput
            {...required('password')}
            type="password"
            name="password"
            label="profile.password"
            placeholder="general.placeholder.create_password"
            error={errors?.password?.message}
            inputSize={inputSize}
          />
        )}
        <MarketingOffers optional={optional} offersLabel={offersLabel} />
        <Divider />
        <FlexStack inline>
          <FormCheckbox
            data-testId="acceptTerms"
            {...required('acceptsTerms')}
          />
          <IAgreeToTermsText label={termsLabel} />
        </FlexStack>
        {isCaptchaRequired && (
          <FormReCAPTCHA
            {...required('captcha')}
            name="captcha"
            control={control}
            error={errors?.captcha?.message}
            recaptchaRef={recaptchaRef}
          />
        )}
        {errors?.global?.message && (
          <FormError
            message={errors?.global?.message}
            data-testid={errors?.global?.message}
          />
        )}
        <SubmitButtonFormItem
          loading={loading}
          isSubmitDisabled={isSubmitDisabled}
          handleSubmit={handleSubmit}
          dataTestId="finish-signup-submit"
          translateId="profile.create_account"
        />
      </Stack>
    </form>
  );
};

export default FinishSignupStep;
